<template>
  <block-item-card
    ref="depositConcentration"
    :title="$t('ALM.Section.DepositsPortfolio.DepositsConcentration.Title')"
    class="fill-height d-flex flex-column"
  >
    <v-col class="pa-4 pt-2">
      <div class="deposit-type-navigation">
        <asset-button-group
          :items="depositTypes"
          :selected.sync="selectedDepositType"
          :mandatory="true"
          :full-width="true"
        />
      </div>

      <!-- Visual Concentration Bar -->
      <div class="">
        <div class="concentration-bar">
          <v-tooltip
            v-for="(segment, index) in concentrationSegments"
            :key="index"
            bottom
            :open-delay="200"
            :close-delay="100"
          >
            <template #activator="{on, attrs}">
              <div
                class="concentration-segment"
                :style="{
                  width: `${segment.percentage}%`,
                  backgroundColor: themeColorRGBA(segment.color, 1),
                  borderRadius: '4px'
                }"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <div class="tooltip-content">
              <div class="tooltip-title">
                {{ segment.label }}
              </div>
              <div class="tooltip-details">
                <div class="tooltip-row">
                  <span class="tooltip-label">{{ $t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.TooltipPercentageLabel') }}:</span>
                  <span class="tooltip-value">{{ formatPercentage(segment.percentage) === '–' ? '–' : `${formatPercentage(segment.percentage)}%` }}</span>
                </div>
                <div class="tooltip-row">
                  <span class="tooltip-label">{{ $t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.TooltipValueLabel') }}:</span>
                  <span class="tooltip-value">{{ formatAmount(segment.value) }}</span>
                </div>
              </div>
            </div>
          </v-tooltip>
        </div>
      </div>

      <!-- Data Display Toggle -->
      <div class="data-toggle-container mb-4 mt-4">
        <!-- <banking-switch
          v-model="showPercentage"
          :enabled-label="$t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.SwitchTotalLabel')"
          :disabled-label="$t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.SwitchPercentageLabel')"
        /> -->
      </div>

      <!-- Concentration Table -->
      <div class="">
        <v-row no-gutters>
          <v-col
            class="table-header synthesis-brand-dark-blue--text pa-1 pl-4"
            cols="4"
          >
            {{ $t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.ConcentrationsTitle') }}
          </v-col>
          <v-col
            cols="8"
            class="d-flex justify-space-between"
          >
            <div class="table-header synthesis-brand-dark-blue--text pa-1 pl-4">
              {{ $t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.PercentageOfContracts') }}
            </div>
            <div class="table-header synthesis-brand-dark-blue--text pa-1 pr-4">
              {{ $t('ALM.Section.DepositsPortfolio.DepositsConcentration.Labels.TotalValueOfDeposits') }}
            </div>
          </v-col>
        </v-row>

        <v-row
          v-for="(split, index) in splits"
          :key="`alm-deposits-portfolio-deposit-concentration-legend-${index}`"
          no-gutters
        >
          <v-col
            class=" synthesis-brand-dark-blue--text  pr-1"
            cols="4"
          >
            <item-chart-legend
              :key="`alm-deposits-portfolio-deposit-concentration-legend-size-${index}`"
              :background-color="themeColorRGBA(getColorByIndex(index), 1)"
              :title="split"
              :title-size="14"
              icon="mdi-circle"
              :icon-color="themeColorRGBA(getIconColorByIndex(index), 1)"
            />
          </v-col>
          <v-col
            cols="8"
          >
            <item-chart-legend
              :key="`alm-deposits-portfolio-deposit-concentration-legend-size-${index}`"
              :background-color="themeColorRGBA(getColorByIndex(index), 1)"
              :title="formatPercentage(perSplitConcentrationData[index]['percentageOfContracts']) === '–' ? '–' : `${formatPercentage(perSplitConcentrationData[index]['percentageOfContracts'])}%`"
              :title-value="formatAmount(perSplitConcentrationData[index]['totalDepositsValue'])"
              :title-size="14"
              :title-value-size="14"
              :title-value-class="'font-weight-medium d-flex align-center'"
            />
          </v-col>
        </v-row>

        <v-row no-gutters>
          <v-col
            class=" synthesis-brand-dark-blue--text  pr-1"
            cols="4"
          >
            <item-chart-legend
              :key="`alm-deposits-portfolio-deposit-concentration-legend-size`"
              :title="totalConcentrationData['splitName']"

              :title-size="14"
              :title-class="'font-weight-bold d-flex align-center'"
              icon="mdi-circle"
              :icon-color="themeColorRGBA('synthesis-ui-grey-02', 1)"
              :border="`1px solid ${themeColorRGBA('synthesis-ui-grey-02', 1)}`"
            />
          </v-col>
          <v-col
            cols="8"
          >
            <item-chart-legend
              :key="`alm-deposits-portfolio-deposit-concentration-legend-size`"
              :title="formatPercentage(totalConcentrationData['percentageOfContracts']) === '–' ? '–' : `${formatPercentage(totalConcentrationData['percentageOfContracts'])}%`"
              :title-size="14"
              :title-class="'font-weight-bold d-flex align-center'"
              :title-value="formatAmount(totalConcentrationData['totalDepositsValue'])"
              :title-value-size="14"
              :title-value-class="'font-weight-bold d-flex align-center'"
              :border="`1px solid ${themeColorRGBA('synthesis-ui-grey-02', 1)}`"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import baseEChartColors from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'
import i18nRouteMeta from '@/mixins/i18n/i18nRouteMeta'
import riskBaseCurrency from '@/theme/default/views/risks/mixins/riskBaseCurrency'
import { toNumberFormatNotationShort, toDecimalSignificant } from '@/lib/utils/helper'
import riskAnalysisId from '@/theme/default/views/risks/mixins/riskAnalysisId'
import AssetButtonGroup from '@/theme/default/components/common/AssetButtonGroup'
import ItemChartLegend from '@/theme/default/components/common/ItemChartLegend'

export default {
  name      : 'ALMDepositConcentration',
  components: { BlockItemCard, AssetButtonGroup, ItemChartLegend },
  directives: {},
  mixins    : [i18nRouteMeta, riskBaseCurrency, baseEChartColors, riskAnalysisId],
  props     : {},
  dataStore : {
    appToolbarExtended  : 'App.Config.AppToolbarExtended',
    Deposits            : 'ALM.Deposits.Data',
    riskBaseCurrencyData: 'Risks.BaseCurrency.Data'
  },
  enums: {},
  data () {
    return {
      selectedDepositType      : 'Total',
      showPercentage           : true,
      breakpoint               : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      minBlockHeight           : 364,
      splits                   : [],
      totalConcentrationData   : [],
      perSplitConcentrationData: [],
      concentrationSegments    : []
    }
  },
  computed: {
    depositTypes () {
      return this.Deposits.DepositsConcentration.typeNames.map(type => ({
        Value: type,
        Title: type
      }))
    },
    currencySymbol () {
      return this.Deposits.BaseCurrencyDisplaySymbol
    }
  },
  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },
    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    },
    selectedDepositType (newType) {
      this.onDepositTypeChange(newType)
    },
    showPercentage (newValue) {
      this.onDepositTypeChange(this.selectedDepositType)
    },
    Deposits: {
      handler (newDeposits) {
        // Recalculate when Deposits data changes (e.g., when portfolio date changes)
        if (newDeposits?.DepositsConcentration?.perTypeConcentrationData?.length > 0 && this.selectedDepositType) {
          this.$nextTick(() => {
            this.onDepositTypeChange(this.selectedDepositType)
          })
        }
      },
      deep     : false,
      immediate: false
    }
  },
  created () {},
  beforeMount () {},
  mounted () {
    this.onDepositTypeChange('Total')
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {
    formatNumberShort (value) {
      return this.currencySymbol + '' + toNumberFormatNotationShort(value)
    },

    formatPercentage (value) {
      if (value === null || value === undefined) {
        return '–'
      }
      return value === 0 ? '0' : value.toString()
    },

    formatAmount (value) {
      if (value === null || value === undefined) {
        return '–'
      }
      return value === 0 ? '0' : this.formatNumberShort(value)
    },

    onDepositTypeChange (depositType) {
      // Handle deposit type selection
      if (!this.Deposits?.DepositsConcentration?.perTypeConcentrationData?.length) {
        return
      }

      const perTypeConcentrationData = this.Deposits.DepositsConcentration.perTypeConcentrationData.find(item => item.typeName === depositType)

      if (!perTypeConcentrationData) {
        return
      }

      this.splits = perTypeConcentrationData.splits || []
      this.totalConcentrationData = perTypeConcentrationData.totalConcentrationData || {}
      this.perSplitConcentrationData = perTypeConcentrationData.perSplitConcentrationData || []

      this.concentrationSegments = []
      if (!this.showPercentage) {
        this.concentrationSegments = this.perSplitConcentrationData.map((item, index) => ({
          label     : item.splitName,
          percentage: item.percentageOfContracts,
          color     : this.getIconColorByIndex(index),
          value     : item.totalDepositsValue
        }))
      } else {
        const totalValue = this.totalConcentrationData.totalDepositsValue
        this.concentrationSegments = this.perSplitConcentrationData.filter(item => item.percentageOfContracts !== null).map((item, index) => {
          let percentage = 0
          if (totalValue && totalValue !== null && totalValue !== 0) {
            const itemValue = item.totalDepositsValue ?? 0
            percentage = toDecimalSignificant(itemValue / totalValue * 100, 2)
          }
          return {
            label     : item.splitName,
            percentage: percentage,
            color     : this.getIconColorByIndex(index),
            value     : item.totalDepositsValue
          }
        })
      }
    },

    getColorByIndex (index) {
      switch (index) {
        case 0:
          return 'synthesis-ui-yellow-08'
        case 1:
          return 'synthesis-ui-green-08'
        case 2:
          return 'synthesis-ui-orange-08'
        default:
          return 'synthesis-ui-grey-08'
      }
    },
    getIconColorByIndex (index) {
      switch (index) {
        case 0:
          return 'synthesis-ui-yellow-01'
        case 1:
          return 'synthesis-ui-green-01'
        case 2:
          return 'synthesis-ui-orange-01'
        default:
          return 'synthesis-ui-grey-01'
      }
    }
  }
}
</script>

<style scoped>
  .deposit-type-navigation {
  display: flex;
  margin-bottom: 24px;
  width: 100%;
}

  .concentration-bar-container {
  margin-bottom: 24px;
}

.concentration-bar {
  height: 18px;
  background-color: var(--v-synthesis-ui-grey-06-base);
  gap: 2px;
  overflow: hidden;
  display: flex;

}

.concentration-segment {
  height: 100%;
  transition: all 0.3s ease;
  cursor: pointer;
}

.concentration-segment:hover {
  filter: brightness(1.1);
}

.data-toggle-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
}

.toggle-label {
  font-size: 14px;
  color: var(--v-synthesis-ui-grey-01-base);
  font-weight: 500;
}

.data-toggle {
  margin: 0;
}

.table-header {
  font-size: 12px;
  font-weight: 400;

}

.concentration-table {
  background: transparent;
}

.concentration-table th {
  background-color: transparent !important;
  color: var(--v-synthesis-ui-grey-01-base);
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid var(--v-synthesis-ui-grey-05-base);
}

.concentration-table td {
  background-color: transparent !important;
  border-bottom: none;
  padding: 16px 8px;
}

.concentration-label {
  width: 40%;
}

.label-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.indicator-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.percentage-value {
  width: 30%;
  font-weight: 500;
  color: var(--v-synthesis-ui-grey-01-base);
}

.total-value {
  width: 30%;
  font-weight: 600;
  color: var(--v-synthesis-ui-grey-01-base);
}

.concentration-row-0 {
  background-color: rgba(255, 152, 0, 0.1) !important;
}

.concentration-row-1 {
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.concentration-row-2 {
  background-color: rgba(255, 87, 34, 0.1) !important;
}

.concentration-row-3 {
  background-color: white !important;
  border: 1px solid var(--v-synthesis-ui-grey-05-base);
  border-radius: 4px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .concentration-table th,
  .concentration-table td {
    font-size: 12px;
    padding: 12px 4px;
  }

  .concentration-table-container {
    padding: 16px;
  }
}

/* Tooltip styles */
.tooltip-content {

  max-width: 200px;
}

.tooltip-title {
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  margin-bottom: 6px;
  text-align: center;
}

.tooltip-details {
  font-size: 12px;
}

.tooltip-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.tooltip-row:last-child {
  margin-bottom: 0;
}

.tooltip-label {
  color: rgba(255, 255, 255, 0.8);
  margin-right: 8px;
}

.tooltip-value {
  color: #ffffff;
  font-weight: 500;
}
</style>
